<template>
  <article
    class="lpu-promotion-card d-flex flex-column ga-4 py-6"
    data-qa="lpu_promotions_card"
  >
    <div class="d-flex flex-column ga-3">
      <h3
        class="text--text text-break ma-0"
        :class="isMobile ? 'text-subtitle-1' : 'text-h6'"
      >
        <template v-if="canEdit && editHref">
          <span v-if="statusPrefix">{{ statusPrefix }}</span>
          <a
            class="lpu-promotion-card__edit"
            :href="editHref"
          >
            {{ title }}
          </a>
        </template>
        <template v-else>
          {{ title }}
        </template>
        <a
          v-if="adminHref"
          :href="adminHref"
          class="adminka ml-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          A
        </a>
      </h3>
      <div
        v-if="remainingLabel || dateRangeLabel"
        class="d-flex justify-space-between"
      >
        <span
          v-if="remainingLabel"
          class="text-body-1 text-secondary--text"
        >
          {{ remainingLabel }}
        </span>
        <span
          v-if="dateRangeLabel"
          class="text-body-1 text-secondary--text"
        >
          {{ dateRangeLabel }}
        </span>
      </div>
    </div>

    <div
      v-if="textHtml"
      class="lpu-promotion-card__text bg-gray-40 rounded-20 text-body-1 text--text"
      :class="isMobile ? 'pa-4' : 'pa-6'"
      v-html="textHtml/* eslint-disable-line vue/no-v-html */"
    />

    <p
      v-if="starText"
      class="text-secondary--text ma-0"
      :class="isMobile ? 'text-body-2' : 'text-body-1'"
    >
      * - {{ starText }}
    </p>

    <div
      v-if="formattedPhone"
      class="d-flex"
      :class="isMobile ? 'flex-column ga-3' : 'align-center ga-6'"
    >
      <span class="text-body-1 text-secondary--text">
        {{ tStatic('Запись по телефону') }}:
      </span>
      <a
        v-if="isMobile && rawPhoneHref"
        class="d-flex align-center ga-2 text-decoration-none text--text"
        :href="rawPhoneHref"
      >
        <span
          class="ui-icon-phone-call ui-icon-fz-smaller flex-shrink-0 text-lpu-branding-color-primary"
          aria-hidden="true"
        />
        <span class="text-body-1">
          {{ formattedPhone }}
        </span>
      </a>
      <div
        v-else
        class="d-flex align-center ga-2 text--text"
      >
        <span
          class="ui-icon-phone-call ui-icon-fz-smaller flex-shrink-0 text-lpu-branding-color-primary"
          aria-hidden="true"
        />
        <span class="text-body-1">
          {{ formattedPhone }}
        </span>
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { LpuPromotion } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/types'
import { PROMOTION_STATUS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/constants'
import canEditLpuPromotion from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/canEditLpuPromotion'
import formatPromotionDate from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/formatPromotionDate'
import getPromotionPhone from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/getPromotionPhone'
import getPromotionTextHtml from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/getPromotionTextHtml'

const props = defineProps<{
  promotion: LpuPromotion
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const { reverse } = useURLStore()

const title = computed(() => tDynamic(props.promotion, 'title'))
const starText = computed(() => tDynamic(props.promotion, 'star_text'))
const remainingLabel = computed(() => tDynamic(props.promotion, 'days_left'))
const textHtml = computed(() => getPromotionTextHtml(tDynamic(props.promotion, 'text')))
const dateStart = computed(() => formatPromotionDate(props.promotion.date_start))
const dateEnd = computed(() => formatPromotionDate(props.promotion.date_end))
const dateRangeLabel = computed(() => {
  if (props.promotion.archive || !dateStart.value || !dateEnd.value) {
    return ''
  }

  return `${dateStart.value} — ${dateEnd.value}`
})
const phoneValue = computed(() => props.promotion.phone?.phone)
const phoneView = computed(() => getPromotionPhone(phoneValue.value, context.request.country))
const formattedPhone = computed(() => phoneView.value.formatted)
const rawPhoneHref = computed(() => phoneView.value.rawHref)
const canEdit = computed(() => canEditLpuPromotion({
  isMobile: isMobile.value,
  editLink: context.edit_link,
  isSuperuser: context.request.user.is_superuser,
  user2Id: context.request.user.user2?.id,
  ownerId: props.lpu.owner?.id ?? props.lpu.owner_id,
}))
const statusPrefix = computed(() => {
  if (!canEdit.value) {
    return ''
  }

  if (props.promotion.status === PROMOTION_STATUS.DRAFT) {
    return `${tStatic('Черновик')}: `
  }

  if (props.promotion.status === PROMOTION_STATUS.MODERATION) {
    return `${tStatic('Модерация')}: `
  }

  return ''
})
const editHref = computed(() => {
  if (!canEdit.value) {
    return ''
  }

  return reverse({
    name: 'profile_lpus:edit_promotion',
    args: [props.promotion.id],
  })
})
const adminHref = computed(() => {
  const isRealSuperuser = Boolean(
    context.request.real_user?.is_superuser
    ?? context.request.user.is_superuser,
  )

  if (isMobile.value || !isRealSuperuser) {
    return ''
  }

  return reverse({
    name: 'admin:promotions_promotion_change',
    args: [props.promotion.id],
  })
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-promotion-card {
  &__edit {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: var(--lpu-branding-color-primary, $ui-kit-primary);
    }
  }

  &__text {
    :deep(ul),
    :deep(ol) {
      margin: 0;
      padding-left: 24px;
    }

    :deep(li) {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
